/**
 * Utility functions for formatting data display
 */

/**
 * Format currency with commas for thousands and proper decimal places
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'USD')
 * @param showCents - Whether to show cents (default: false for whole numbers)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | null | undefined, 
  currency: string = 'USD',
  showCents: boolean = false
): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'N/A';
  }

  // Determine if we should show cents
  const shouldShowCents = showCents || (amount % 1 !== 0);
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: shouldShowCents ? 2 : 0,
      maximumFractionDigits: shouldShowCents ? 2 : 0,
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    const formattedNumber = amount.toLocaleString('en-US', {
      minimumFractionDigits: shouldShowCents ? 2 : 0,
      maximumFractionDigits: shouldShowCents ? 2 : 0,
    });
    return `$${formattedNumber}`;
  }
};

/**
 * Format a simple dollar amount with commas (no currency symbol)
 * @param amount - The amount to format
 * @param showCents - Whether to show cents
 * @returns Formatted number string with commas
 */
export const formatDollarAmount = (
  amount: number | null | undefined,
  showCents: boolean = false
): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'N/A';
  }

  const shouldShowCents = showCents || (amount % 1 !== 0);
  
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: shouldShowCents ? 2 : 0,
    maximumFractionDigits: shouldShowCents ? 2 : 0,
  });
};

/**
 * Format a number with commas (for non-currency values)
 * @param value - The number to format
 * @param decimals - Number of decimal places to show
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals: number = 0
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format percentage values
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | null | undefined,
  decimals: number = 1
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

/**
 * Format wage per hour
 * @param hourlyWage - The hourly wage amount
 * @returns Formatted wage string
 */
export const formatHourlyWage = (hourlyWage: number | null | undefined): string => {
  if (hourlyWage === null || hourlyWage === undefined || isNaN(hourlyWage)) {
    return 'N/A';
  }

  return `${formatCurrency(hourlyWage, 'USD', true)}/hr`;
};

/**
 * Format large numbers with appropriate units (K, M, B)
 * @param value - The number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number with units
 */
export const formatLargeNumber = (
  value: number | null | undefined,
  decimals: number = 1
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(decimals)}B`;
  } else if (value >= 1000000) {
    return `${(value / 1000000).toFixed(decimals)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(decimals)}K`;
  } else {
    return value.toFixed(decimals);
  }
}; 